/**
 * BodyReach — how far an attack has to reach to touch a BODY, not its centre.
 *
 * Plan 07/08 reach audit (2026-10-07): every player reach check measured from
 * the attacker's centre to the target's CENTRE. That is right for a 40x40
 * slime and wrong for anything big: standing flush against the 84x96 Moonfall
 * golem its centre is 62 px away, against the 180x130 `pig_mom` 110 px — so
 * every cone, cast range and target query lost half a body's width of reach
 * exactly on the bosses players are supposed to fight. The fix is to measure
 * to the nearest point of the target's box (zero when you stand inside it).
 *
 * Bodies here are CENTRED: `{x, y}` is the combat point (`mobCombatPoint` /
 * `mobSpriteCenter`), `width`/`height` the hitbox. A missing or zero size is a
 * point body, so callers without dimensions keep the old centre measure.
 *
 * Pure, no imports: shared by client prediction, server authority, the danger
 * sim and tests.
 */

export interface ReachPoint {
  x: number;
  y: number;
}

/** A centred body: combat point plus optional hitbox size. */
export interface ReachBody extends ReachPoint {
  width?: number;
  height?: number;
}

function halfExtents(body: ReachBody): { hw: number; hh: number } {
  return {
    hw: body.width && body.width > 0 ? body.width / 2 : 0,
    hh: body.height && body.height > 0 ? body.height / 2 : 0,
  };
}

/** The point of `body`'s box nearest to `from` (`from` itself when inside). */
export function nearestBodyPoint(from: ReachPoint, body: ReachBody): ReachPoint {
  const { hw, hh } = halfExtents(body);
  return {
    x: Math.max(body.x - hw, Math.min(body.x + hw, from.x)),
    y: Math.max(body.y - hh, Math.min(body.y + hh, from.y)),
  };
}

/** Distance from `from` to the edge of `body` (0 when inside it). */
export function reachToBody(from: ReachPoint, body: ReachBody): number {
  const p = nearestBodyPoint(from, body);
  return Math.hypot(p.x - from.x, p.y - from.y);
}

/** Does the segment a→b touch `body`'s box? (Slab test.) */
export function segmentTouchesBody(a: ReachPoint, b: ReachPoint, body: ReachBody): boolean {
  const { hw, hh } = halfExtents(body);
  let t0 = 0;
  let t1 = 1;
  const axes: Array<[number, number, number, number]> = [
    [a.x, b.x - a.x, body.x - hw, body.x + hw],
    [a.y, b.y - a.y, body.y - hh, body.y + hh],
  ];
  for (const [start, delta, min, max] of axes) {
    if (Math.abs(delta) < 1e-12) {
      if (start < min || start > max) return false;
      continue;
    }
    let ta = (min - start) / delta;
    let tb = (max - start) / delta;
    if (ta > tb) [ta, tb] = [tb, ta];
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return false;
  }
  return true;
}
